import {
  cardStrength,
  createDeck,
  effectiveSuit,
  hashString,
  isLeftBower,
  isRightBower,
  teamOf,
} from "./cards";
import type { Card, Difficulty, GameAction, PublicView, Rank, Seat, Suit } from "./types";

const SUIT_ORDER: Suit[] = ["clubs", "diamonds", "hearts", "spades"];

// Effective trump cards per suit in a 24-card deck: the six suited cards plus the Left Bower.
const TRUMP_TOTAL = 7;

// Calling strength thresholds per difficulty. "relaxed" plays cautiously: it
// needs a stronger hand before ordering up or calling, and almost never goes
// alone. "sharp" keeps the standard thresholds but earns its edge from the
// seen-card adjustments below.
const ORDER_THRESHOLD: Record<Difficulty, number> = { relaxed: 11, standard: 9, sharp: 9 };
const CALL_THRESHOLD: Record<Difficulty, number> = { relaxed: 10, standard: 8, sharp: 8 };
const ALONE_THRESHOLD: Record<Difficulty, number> = { relaxed: 17, standard: 15, sharp: 15 };

function trumpPower(hand: Card[], trump: Suit): number {
  return hand.reduce((score, card) => {
    if (isRightBower(card, trump)) return score + 8;
    if (isLeftBower(card, trump)) return score + 7;
    if (effectiveSuit(card, trump) === trump) {
      return score + ({ A: 5, K: 4, Q: 3, J: 2, "10": 2, "9": 1 }[card.rank] ?? 0);
    }
    return score + (card.rank === "A" ? 1.5 : 0);
  }, 0);
}

function stableTie(view: PublicView, key: string): number {
  return hashString(`${view.actionNonce}:${view.seat}:${key}`);
}

function trumpCards(hand: Card[], trump: Suit): Card[] {
  return hand.filter((card) => effectiveSuit(card, trump) === trump);
}

function offAces(hand: Card[], trump: Suit): number {
  return hand.filter((card) => card.rank === "A" && effectiveSuit(card, trump) !== trump).length;
}

function orderScore(view: PublicView, difficulty: Difficulty): number {
  let score = trumpPower(view.ownHand, view.upcard.suit) + (view.seat === view.dealer ? 1.5 : 0);
  if (difficulty === "sharp" && view.dealer !== view.seat) {
    // Sharp reads the seen upcard: the dealer picks it up, so a strong upcard
    // strengthens whichever partnership holds the deal.
    const upcardValue =
      view.upcard.rank === "A" || view.upcard.rank === "J"
        ? 1
        : view.upcard.rank === "K" || view.upcard.rank === "Q"
          ? 0.5
          : 0;
    score += teamOf(view.dealer) === teamOf(view.seat) ? upcardValue : -upcardValue;
  }
  return score;
}

function callCandidates(view: PublicView, difficulty: Difficulty): { suit: Suit; score: number }[] {
  return SUIT_ORDER.filter((suit) => suit !== view.upcard.suit)
    .map((suit) => {
      let score = trumpPower(view.ownHand, suit);
      if (difficulty === "sharp" && isLeftBower(view.upcard, suit)) {
        // The turned-down upcard is out of play; when it is this suit's Left
        // Bower, the suit permanently loses its second-strongest card.
        score -= 1.5;
      }
      return { suit, score };
    })
    .sort((a, b) => b.score - a.score || stableTie(view, a.suit) - stableTie(view, b.suit));
}

function sortedLegal(view: PublicView, trump: Suit, lead: Suit): Card[] {
  const legal = view.ownHand.filter((card) => view.legalCardIds.includes(card.id));
  return [...legal].sort(
    (a, b) =>
      cardStrength(a, trump, lead) - cardStrength(b, trump, lead) ||
      stableTie(view, a.id) - stableTie(view, b.id),
  );
}

function playedCards(view: PublicView): Card[] {
  return [
    ...view.completedTricks.flatMap((trick) => trick.cards.map((entry) => entry.card)),
    ...view.currentTrick.map((entry) => entry.card),
  ];
}

function unseenStrongerTrump(view: PublicView, trump: Suit, card: Card): number {
  const seen = new Set([...view.ownHand.map((entry) => entry.id), ...playedCards(view).map((entry) => entry.id)]);
  const strength = cardStrength(card, trump, trump);
  return createDeck().filter(
    (entry) =>
      effectiveSuit(entry, trump) === trump &&
      !seen.has(entry.id) &&
      cardStrength(entry, trump, trump) > strength,
  ).length;
}

function trumpOutside(view: PublicView, trump: Suit): number {
  const own = trumpCards(view.ownHand, trump).length;
  const played = playedCards(view).filter((card) => effectiveSuit(card, trump) === trump).length;
  return Math.max(0, TRUMP_TOTAL - own - played);
}

function standardLead(view: PublicView, trump: Suit, sorted: Card[]): Card {
  const trumps = sorted.filter((card) => effectiveSuit(card, trump) === trump);
  return trumps.at(-1) ?? sorted.at(-1)!;
}

function sharpLead(view: PublicView, trump: Suit, sorted: Card[]): Card {
  const trumps = sorted.filter((card) => effectiveSuit(card, trump) === trump);
  if (!trumps.length) return sorted.at(-1)!;
  const top = trumps.at(-1)!;
  const outside = trumpOutside(view, trump);
  if (outside > 0 && unseenStrongerTrump(view, trump, top) === 0) {
    // Draw trump: the boss trump pulls the opponents' remaining trump.
    return top;
  }
  // Do not waste a non-boss trump (or trump nobody can follow); lead the best side card.
  const off = sorted.filter((card) => effectiveSuit(card, trump) !== trump);
  return off.at(-1) ?? top;
}

function currentTrickBest(view: PublicView, trump: Suit, lead: Suit) {
  return view.currentTrick.reduce((winner, entry) =>
    cardStrength(entry.card, trump, lead) > cardStrength(winner.card, trump, lead) ? entry : winner,
  );
}

function followChoice(view: PublicView, trump: Suit, lead: Suit, sorted: Card[]): Card {
  const currentBest = currentTrickBest(view, trump, lead);
  const partnerWinning = teamOf(currentBest.seat) === teamOf(view.seat);
  const winning = sorted.filter(
    (card) => cardStrength(card, trump, lead) > cardStrength(currentBest.card, trump, lead),
  );
  return partnerWinning ? sorted[0] : (winning[0] ?? sorted[0]);
}

function relaxedChoice(view: PublicView, sorted: Card[], chosen: Card): Card {
  const index = sorted.findIndex((card) => card.id === chosen.id);
  const shifted = sorted[Math.max(index - 1, 0)];
  if (shifted.id === chosen.id) return chosen;
  // Deterministic one-in-four slip keyed on position and card; never Math.random.
  return hashString(`${view.actionNonce}:${view.seat}:relaxed:${chosen.id}`) % 4 === 0 ? shifted : chosen;
}

export type CoachAdvice =
  | { kind: "order"; suit: Suit }
  | { kind: "pass" }
  | { kind: "call"; suit: Suit }
  | { kind: "wait" }
  | { kind: "follow"; cardId: string; rank: Rank; suit: Suit }
  | { kind: "balance"; cardId: string; rank: Rank }
  | { kind: "observe" };

export interface CallExplanation {
  kind: "order" | "call" | "pass";
  suit: Suit | null;
  trumpCount: number;
  hasRight: boolean;
  hasLeft: boolean;
  offAces: number;
  dealerEdge: boolean;
  threshold: number;
  score: number;
}

export interface DiscardExplanation {
  kind: "discard";
  cardId: string;
  trump: Suit;
}

export interface LeadExplanation {
  kind: "lead";
  cardId: string;
  trumpCount: number;
  drawTrump: boolean;
}

export interface PlayExplanation {
  kind: "follow" | "trump" | "duck";
  cardId: string;
  leadSuit: Suit;
  partnerWinning: boolean;
  forcedFollow: boolean;
  winningTry: boolean;
  trumpCount: number;
}

export type AiExplanation = CallExplanation | DiscardExplanation | LeadExplanation | PlayExplanation;

export function chooseAiAction(view: PublicView, difficulty: Difficulty = "standard"): GameAction {
  if (view.phase === "calling1") {
    const score = orderScore(view, difficulty);
    if (score >= ORDER_THRESHOLD[difficulty]) {
      return { type: "ORDER_UP", seat: view.seat, alone: score >= ALONE_THRESHOLD[difficulty] };
    }
    return { type: "PASS", seat: view.seat };
  }
  if (view.phase === "calling2") {
    const best = callCandidates(view, difficulty)[0];
    if (best.score >= CALL_THRESHOLD[difficulty] || view.seat === view.dealer) {
      return { type: "CALL_SUIT", seat: view.seat, suit: best.suit, alone: best.score >= ALONE_THRESHOLD[difficulty] };
    }
    return { type: "PASS", seat: view.seat };
  }
  if (view.phase === "discard") {
    const trump = view.upcard.suit;
    const card = [...view.ownHand].sort(
      (a, b) =>
        cardStrength(a, trump, trump) - cardStrength(b, trump, trump) ||
        stableTie(view, a.id) - stableTie(view, b.id),
    )[0];
    return { type: "DISCARD", seat: view.seat, cardId: card.id };
  }
  if (view.phase === "playing" && view.trump) {
    const trump = view.trump;
    const lead = view.currentTrick.length ? effectiveSuit(view.currentTrick[0].card, trump) : trump;
    const sorted = sortedLegal(view, trump, lead);
    let chosen = view.currentTrick.length
      ? followChoice(view, trump, lead, sorted)
      : difficulty === "sharp"
        ? sharpLead(view, trump, sorted)
        : standardLead(view, trump, sorted);
    if (difficulty === "relaxed") chosen = relaxedChoice(view, sorted, chosen);
    return { type: "PLAY_CARD", seat: view.seat, cardId: chosen.id };
  }
  throw new Error("AI requested outside an actionable phase");
}

export function explainAction(
  view: PublicView,
  action: GameAction,
  difficulty: Difficulty = "standard",
): AiExplanation {
  if (action.type === "ORDER_UP" || (action.type === "PASS" && view.phase === "calling1")) {
    const trump = view.upcard.suit;
    return {
      kind: action.type === "ORDER_UP" ? "order" : "pass",
      suit: trump,
      trumpCount: trumpCards(view.ownHand, trump).length,
      hasRight: view.ownHand.some((card) => isRightBower(card, trump)),
      hasLeft: view.ownHand.some((card) => isLeftBower(card, trump)),
      offAces: offAces(view.ownHand, trump),
      dealerEdge: view.seat === view.dealer,
      threshold: ORDER_THRESHOLD[difficulty],
      score: orderScore(view, difficulty),
    };
  }
  if (action.type === "CALL_SUIT" || (action.type === "PASS" && view.phase === "calling2")) {
    const suit = action.type === "CALL_SUIT" ? action.suit : callCandidates(view, difficulty)[0].suit;
    const score = callCandidates(view, difficulty).find((candidate) => candidate.suit === suit)?.score
      ?? trumpPower(view.ownHand, suit);
    return {
      kind: action.type === "CALL_SUIT" ? "call" : "pass",
      suit,
      trumpCount: trumpCards(view.ownHand, suit).length,
      hasRight: view.ownHand.some((card) => isRightBower(card, suit)),
      hasLeft: view.ownHand.some((card) => isLeftBower(card, suit)),
      offAces: offAces(view.ownHand, suit),
      dealerEdge: view.seat === view.dealer,
      threshold: CALL_THRESHOLD[difficulty],
      score,
    };
  }
  if (action.type === "DISCARD") {
    return { kind: "discard", cardId: action.cardId, trump: view.upcard.suit };
  }
  if (action.type === "PLAY_CARD" && view.trump) {
    const trump = view.trump;
    const card = view.ownHand.find((entry) => entry.id === action.cardId);
    if (!card) throw new Error("Cannot explain a card outside the seat's hand");
    const trumpCount = trumpCards(view.ownHand, trump).length;
    if (!view.currentTrick.length) {
      return {
        kind: "lead",
        cardId: card.id,
        trumpCount,
        drawTrump:
          effectiveSuit(card, trump) === trump &&
          trumpOutside(view, trump) > 0 &&
          unseenStrongerTrump(view, trump, card) === 0,
      };
    }
    const leadSuit = effectiveSuit(view.currentTrick[0].card, trump);
    const currentBest = currentTrickBest(view, trump, leadSuit);
    const partnerWinning = teamOf(currentBest.seat) === teamOf(view.seat);
    const forcedFollow = view.legalCardIds.length > 0 && view.legalCardIds.length < view.ownHand.length;
    const winningTry = cardStrength(card, trump, leadSuit) > cardStrength(currentBest.card, trump, leadSuit);
    const kind = winningTry
      ? effectiveSuit(card, trump) === trump && leadSuit !== trump
        ? "trump"
        : "follow"
      : "duck";
    return { kind, cardId: card.id, leadSuit, partnerWinning, forcedFollow, winningTry, trumpCount };
  }
  throw new Error("Cannot explain this action in the current phase");
}

export function coachRecommendation(view: PublicView): CoachAdvice {
  if (view.phase === "calling1") {
    const score = trumpPower(view.ownHand, view.upcard.suit);
    return score >= 9 ? { kind: "order", suit: view.upcard.suit } : { kind: "pass" };
  }
  if (view.phase === "calling2") {
    const best = SUIT_ORDER.filter((suit) => suit !== view.upcard.suit)
      .map((suit) => ({ suit, score: trumpPower(view.ownHand, suit) }))
      .sort((a, b) => b.score - a.score)[0];
    return { kind: "call", suit: best.suit };
  }
  if (view.phase === "playing" && view.trump) {
    if (view.currentSeat !== view.seat || view.legalCardIds.length === 0) {
      return { kind: "wait" };
    }
    const action = chooseAiAction(view);
    if (action.type !== "PLAY_CARD") return { kind: "observe" };
    const card = view.ownHand.find((entry) => entry.id === action.cardId)!;
    const forced =
      view.currentTrick.length > 0 &&
      view.legalCardIds.length < view.ownHand.length;
    return forced
      ? { kind: "follow", cardId: card.id, rank: card.rank, suit: effectiveSuit(card, view.trump) }
      : { kind: "balance", cardId: card.id, rank: card.rank };
  }
  return { kind: "observe" };
}

export function seatName(seat: Seat): "south" | "west" | "north" | "east" {
  return (["south", "west", "north", "east"] as const)[seat];
}
